import type { z } from 'zod';
import React from 'react';

import type { DeclaredDetails, ReportSchema } from '@ballerine/common';

import { GoogleMapsView } from '@/components/templates/shared/AddressComponents/GoogleMapsView';
import { DFMerchantDetailsContent } from './DFMerchantDetailsContent';
import { ProvidedDetailsContent } from './ProvidedDetailsContent';

type MerchantDetailsSectionProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'];
  googleMapsApiKey: string;
  providedDetails?: {
    websiteUrl?: string | null;
    companyName?: string | null;
    merchantId?: string | null;
    countryCode?: string | null;
    declaredDetails?: DeclaredDetails;
  } | null;
  detailsComparison?: string | null;
};

export const MerchantDetails: React.FC<MerchantDetailsSectionProps> = ({
  merchantDetails,
  googleMapsApiKey,
  providedDetails,
  detailsComparison,
}) => {
  return (
    <div className="space-y-4 rounded-2xl bg-white pt-10">
      <div className="flex gap-6">
        <div className="flex w-3/5 flex-col">
          <h2 className="mb-3 text-xl font-semibold text-slate-800">Extracted Merchant Details</h2>
          <DFMerchantDetailsContent merchantDetails={merchantDetails} />
        </div>
        <div className="w-2/5">
          <GoogleMapsView
            address={merchantDetails?.address}
            countryCode={undefined}
            googleMapsApiKey={googleMapsApiKey}
            className="h-80"
          />
        </div>
      </div>

      {detailsComparison && (
        <div className="rounded-3xl border border-gray-200 p-6 shadow-md">
          <p className="font-bold text-slate-800">
            Provided vs Extracted Merchant Details Comparison
          </p>
          <p className="text-sm leading-relaxed">{detailsComparison}</p>
        </div>
      )}

      <div className="rounded-3xl border border-[#eeeded] bg-[#fbfcff] px-8 py-6 shadow-[0px_2px_4px_0px_rgba(174,174,174,0.1)]">
        <h3 className="mb-8 text-xl font-semibold text-slate-800">Provided Details</h3>
        <ProvidedDetailsContent providedDetails={providedDetails} />
      </div>
    </div>
  );
};
